// ============================================
// Taiga's own symbols per platform, for the Predictions page and GoPredict S360.
//   MT5:     GET /api/v1/symbol-mappings/mt5-symbols   (all connected MT5 nodes)
//   cTrader: GET /api/v1/ctrader/master -> GET /api/v1/ctrader/nodes/{id}/symbols
//            (master node only, archived symbols left out)
// Fetched once per page load and shared (both lists are reference data).
// ============================================

export type MapPlatform = 'MT5' | 'CTRADER';

export interface PlatformSymbol {
  symbol: string;
  description: string;
  digits: number | null;
}

export interface PlatformSymbols {
  MT5: PlatformSymbol[];
  CTRADER: PlatformSymbol[];
  /** Why a list is empty (not connected, no access, …); null when it loaded. */
  error: Record<MapPlatform, string | null>;
}

const API_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: { 'Content-Type': 'application/json' } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof (err as any)?.error === 'string' ? (err as any).error : `HTTP ${res.status}`);
  }
  return res.json();
}

const norm = (rows: any[]): PlatformSymbol[] => {
  const seen = new Map<string, PlatformSymbol>();
  for (const r of rows ?? []) {
    const symbol = String(r?.symbol ?? '').trim();
    if (!symbol || seen.has(symbol)) continue;
    seen.set(symbol, {
      symbol,
      description: String(r?.description ?? ''),
      digits: typeof r?.digits === 'number' && Number.isFinite(r.digits) ? r.digits : null,
    });
  }
  return [...seen.values()].sort((a, b) => a.symbol.localeCompare(b.symbol));
};

async function loadMt5(): Promise<PlatformSymbol[]> {
  const r = await getJson<{ symbols: any[] }>('/api/v1/symbol-mappings/mt5-symbols');
  return norm(r.symbols);
}

async function loadCtrader(): Promise<PlatformSymbol[]> {
  const m = await getJson<{ master: { node_id: number } | null }>('/api/v1/ctrader/master');
  if (!m.master) throw new Error('cTrader master is not set');
  const r = await getJson<{ symbols: any[] }>(`/api/v1/ctrader/nodes/${m.master.node_id}/symbols`);
  return norm((r.symbols ?? []).filter((s: any) => !s?.archived));
}

let cache: Promise<PlatformSymbols> | null = null;

/** Both platforms' symbol lists; one fetch per page load. `fresh` forces a reload. */
export function loadPlatformSymbols(fresh = false): Promise<PlatformSymbols> {
  if (cache && !fresh) return cache;
  cache = Promise.allSettled([loadMt5(), loadCtrader()]).then(([mt5, ct]) => ({
    MT5:     mt5.status === 'fulfilled' ? mt5.value : [],
    CTRADER: ct.status  === 'fulfilled' ? ct.value  : [],
    error: {
      MT5:     mt5.status === 'rejected' ? (mt5.reason as Error)?.message || 'MT5 symbols unavailable' : null,
      CTRADER: ct.status  === 'rejected' ? (ct.reason  as Error)?.message || 'cTrader symbols unavailable' : null,
    },
  }));
  return cache;
}

/** Digits of a Taiga symbol, or null when unknown. */
export function digitsOf(list: PlatformSymbols | null, platform: MapPlatform, symbol: string): number | null {
  return list?.[platform].find(s => s.symbol === symbol)?.digits ?? null;
}
